/**
 * Scroll-region helpers for the mobile drawer.
 *
 * Two walks, two jobs — do not merge them:
 *   - `findContentScrollRegion` walks *down* from the drawer body for the panel's own
 *     vertical scroll region. Used to infer a content-owned header from where it starts.
 *   - `findScrollableAncestor` walks *up* from a touch target and requires the node to be
 *     *currently* overflowing. Used to tell a drag on the sheet apart from content
 *     scrolling.
 */

// Node budget for the downward walk. Panels put their scroll region within a few levels of
// the body; a budget bounds the cost against content that has none at all, since each step
// reads computed style.
const SCROLL_REGION_NODE_BUDGET = 150;

function isOverflowAutoOrScroll(value: string): boolean {
    return value === "auto" || value === "scroll";
}

function elementChildren(node: HTMLElement): HTMLElement[] {
    const children: HTMLElement[] = [];
    for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i];
        if (child instanceof HTMLElement) {
            children.push(child);
        }
    }
    return children;
}

/**
 * True when `node` is the kind of node that owns *vertical* scrolling.
 *
 * "Scrollable" here means *declared* scrollable rather than currently overflowing — the
 * question is which region owns scrolling, and a short list that does not currently
 * overflow is still that region. It also has to be declared, not measured, because the
 * walk runs before first paint, when a region that will overflow does not yet.
 *
 * The catch is that CSS pairs the overflow axes: `overflow-x: auto` on a chip row or tab
 * strip makes its `overflow-y` compute to `auto` too, so the declaration alone cannot tell
 * that row from a panel body. What separates them is flex sizing — a body is the item that
 * takes the leftover space, a chip row is sized to its content and never grows. Where even
 * that is inconclusive, fall back to whether the node actually overflows vertically.
 */
export function isVerticalScrollRegion(node: HTMLElement): boolean {
    const style = getComputedStyle(node);
    if (!isOverflowAutoOrScroll(style.overflowY)) {
        return false;
    }
    if (!isOverflowAutoOrScroll(style.overflowX)) {
        return true;
    }
    return parseFloat(style.flexGrow) > 0 || node.scrollHeight > node.clientHeight + 1;
}

/**
 * Shallowest vertical scroll region under `root`, or null.
 *
 * Breadth-first on purpose. A panel's scroll region sits at the top level of its layout
 * column, below the non-scrolling header prefix this measurement exists to find; the
 * scrollers that could be mistaken for it (a chip scroller, a compact list, search
 * internals) live *inside* that prefix, deeper down. Taking the shallowest match keeps
 * those from stealing the measurement and clipping the real title.
 */
export function findContentScrollRegion(root: HTMLElement): HTMLElement | null {
    let level = elementChildren(root);
    let visited = 0;

    while (level.length > 0 && visited < SCROLL_REGION_NODE_BUDGET) {
        const next: HTMLElement[] = [];
        for (const node of level) {
            if (visited >= SCROLL_REGION_NODE_BUDGET) {
                break;
            }
            visited += 1;
            if (isVerticalScrollRegion(node)) {
                return node;
            }
            next.push(...elementChildren(node));
        }
        level = next;
    }
    return null;
}

/**
 * Nearest ancestor of `target` inside `root` that currently scrolls *vertically*.
 *
 * Unlike the walk above this one does measure: the question is whether the content would
 * consume this gesture, and content that does not overflow would not.
 *
 * Vertical only, on purpose. The classifier consults the result for one thing — "is the
 * content at its top?" — and a horizontal chip or tab scroller between the finger and the
 * real list would answer that for the wrong element (its `scrollTop` is always 0), letting
 * a downward pull drag the sheet while the list underneath is mid-scroll. Horizontal pans
 * are classified by axis, before this is ever consulted.
 */
export function findScrollableAncestor(target: EventTarget | null, root: HTMLElement): HTMLElement | null {
    let node = target instanceof HTMLElement ? target : null;
    while (node && node !== root) {
        if (isOverflowAutoOrScroll(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
}
